/**
 * Discriminator for the unified clinical-notes feature.
 * Backend enum (byte): PhysicalExam=1, MedicalHistory=2, Counselling=3.
 *
 * Use the string form when constructing API URLs — the backend's
 * ClinicalNoteType enum binds by name in route parameters.
 */
export enum ClinicalNoteType {
  PhysicalExam = 'PhysicalExam',
  MedicalHistory = 'MedicalHistory',
  Counselling = 'Counselling',
}

/**
 * Map a ClinicalNoteType to the tab key used by ClinicalNotesPanel so the
 * matching tab can be restored after returning from a note's edit form.
 */
export const clinicalNotesTabKey = (noteType: ClinicalNoteType): string => {
  switch (noteType) {
    case ClinicalNoteType.MedicalHistory:
      return 'medicalHistory';
    case ClinicalNoteType.Counselling:
      return 'counselling';
    case ClinicalNoteType.PhysicalExam:
    default:
      return 'physicalExam';
  }
};

export interface IClinicalNote {
  id: number;
  noteType: ClinicalNoteType | string;
  examDate: Date | string;
  questionnaireID?: number | null;
  questionnaireName?: string | null;
  patientTaskID?: number | null;
  acknowledgementTaskID?: number | null;
  patientTaskCompleted: boolean;
  patientAnswersJson?: string | null;
  freeTextNote?: string | null;
  answersJson?: string | null;
  lastModifiedDate?: Date | string | null;

  isLocked?: boolean;
  lockedAtUtc?: string | null;
  lockedByHcpID?: number | null;
  lockedByHcpName?: string | null;

  hcpSigned?: boolean;
  hcpSignedAtUtc?: string | null;
  hcpSignedByName?: string | null;
  patientAcknowledged?: boolean;
  patientAcknowledgedAtUtc?: string | null;
  patientAcknowledgedByName?: string | null;
  patientAcknowledgementNotes?: string | null;
}

export interface IClinicalNoteQuestionnaireOption {
  id: number;
  nameEn?: string | null;
  nameFr?: string | null;
}

export class ClinicalNote implements IClinicalNote {
  id: number;
  noteType: ClinicalNoteType;
  examDate: Date;
  questionnaireID: number | null;
  questionnaireName: string | null;
  patientTaskID: number | null;
  acknowledgementTaskID: number | null;
  patientTaskCompleted: boolean;
  patientAnswersJson: string | null;
  freeTextNote: string | null;
  answersJson: string | null;
  lastModifiedDate: Date | null;
  isLocked: boolean;
  lockedAtUtc: string | null;
  lockedByHcpID: number | null;
  lockedByHcpName: string | null;
  hcpSigned: boolean;
  hcpSignedAtUtc: string | null;
  hcpSignedByName: string | null;
  patientAcknowledged: boolean;
  patientAcknowledgedAtUtc: string | null;
  patientAcknowledgedByName: string | null;
  patientAcknowledgementNotes: string | null;

  constructor(data: IClinicalNote) {
    this.id = data.id;
    this.noteType =
      (data.noteType as ClinicalNoteType) ?? ClinicalNoteType.PhysicalExam;
    this.examDate = data.examDate ? new Date(data.examDate) : new Date();
    this.questionnaireID = data.questionnaireID ?? null;
    this.questionnaireName = data.questionnaireName ?? null;
    this.patientTaskID = data.patientTaskID ?? null;
    this.acknowledgementTaskID = data.acknowledgementTaskID ?? null;
    this.patientTaskCompleted = data.patientTaskCompleted ?? false;
    this.patientAnswersJson = data.patientAnswersJson ?? null;
    this.freeTextNote = data.freeTextNote ?? null;
    this.answersJson = data.answersJson ?? null;
    this.lastModifiedDate = data.lastModifiedDate
      ? new Date(data.lastModifiedDate)
      : null;
    this.isLocked = data.isLocked ?? false;
    this.lockedAtUtc = data.lockedAtUtc ?? null;
    this.lockedByHcpID = data.lockedByHcpID ?? null;
    this.lockedByHcpName = data.lockedByHcpName ?? null;
    this.hcpSigned = data.hcpSigned ?? false;
    this.hcpSignedAtUtc = data.hcpSignedAtUtc ?? null;
    this.hcpSignedByName = data.hcpSignedByName ?? null;
    this.patientAcknowledged = data.patientAcknowledged ?? false;
    this.patientAcknowledgedAtUtc = data.patientAcknowledgedAtUtc ?? null;
    this.patientAcknowledgedByName = data.patientAcknowledgedByName ?? null;
    this.patientAcknowledgementNotes = data.patientAcknowledgementNotes ?? null;
  }

  get hasPreVisitQuestionnaire(): boolean {
    return this.questionnaireID !== null && this.patientTaskID !== null;
  }
}

export class ClinicalNoteData {
  clinicalNotes: ClinicalNote[];
  availableQuestionnaires: IClinicalNoteQuestionnaireOption[];

  constructor(data: {
    clinicalNotes: IClinicalNote[];
    availableQuestionnaires: IClinicalNoteQuestionnaireOption[];
  }) {
    this.clinicalNotes = (data.clinicalNotes ?? []).map(
      (e) => new ClinicalNote(e),
    );
    this.availableQuestionnaires = data.availableQuestionnaires ?? [];
  }

  get totalCount(): number {
    return this.clinicalNotes.length;
  }

  questionnaireLabel(
    option: IClinicalNoteQuestionnaireOption,
    language: string,
  ): string {
    const fr = option.nameFr ?? '';
    const en = option.nameEn ?? '';
    return language === 'fr' ? fr || en : en || fr;
  }
}

export interface IClinicalNoteInput {
  id?: number | null;
  examDate: Date | string;
  questionnaireID?: number | null;
  patientTaskID?: number | null;
  freeTextNote?: string | null;
  answersJson?: string | null;
}

export interface IClinicalNoteActivityEvent {
  id: number;
  clinicalNoteID?: number | null;
  action:
    | 'Created'
    | 'Edited'
    | 'Deleted'
    | 'PdfDownloaded'
    | 'SentToAssistant'
    | string;
  actorHcpID?: number | null;
  actorName?: string | null;
  eventDate: string;
  details?: Record<string, unknown> | null;
}
